import AudienceTemplateModalType from '@/Enums/AudienceTemplates/ModalType.enum';
import LineItemFormTab from '@/Enums/CampaignManagement/LineItemFormTab.enum';
import { Channel } from '@/Enums/Channel.enum';
import type { ChannelId } from '@/Enums/ChannelId.enum';
import type { IBaseAudienceTargeting } from '@/Interfaces/Audiences/Audience.interface';
import type { IAudienceControlOption } from '@/Interfaces/ControlOption.interface';
import AudienceTargetingService from '@/Services/AudienceTargeting.service';
import AudienceHeader from '@/components/AudienceHeader';
import AudienceListContainer from '@/components/AudienceList';
import AudienceTemplatesModal from '@/components/AudienceTemplates/AudienceTemplatesModal';
import SupportRequestModal from '@/components/Modal/SupportRequestModal';
import { getFieldErrorProps } from '@/helpers/form';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  selectCampaignManagementAudiences,
  selectFirstPartySegments,
} from '@/store/selectors/campaignManagement.selectors';
import { selectExtendedAudiences } from '@/store/selectors/currentWhitelabel.selectors';
import { selectIsAdmin } from '@/store/selectors/me.selectors';
import { getAudiences, getBeeswaxFirstPartySegments } from '@/store/slices/campaignManagementSlice';
import { Autocomplete, Checkbox, FormLabel, TextField } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';

const LineItemFormAudienceSection = () => {
  const { control, setValue } = useFormContext();
  const dispatch = useAppDispatch();

  const { loadingAudiences, audiences } = useAppSelector(selectCampaignManagementAudiences);
  const { firstPartySegments, loadingFirstPartySegments } = useAppSelector(selectFirstPartySegments);
  const extendedAudiences = useAppSelector(selectExtendedAudiences);
  const isAdmin = useAppSelector(selectIsAdmin);

  const [watchAudiences, watchChannelOption] = useWatch({ control, name: ['audiences', 'channel'] });
  const watchChannel = watchChannelOption?.label as Channel | undefined;
  const watchChannelId = watchChannelOption?.value as ChannelId | undefined;

  const options = useMemo(() => (watchChannel ? (audiences?.[watchChannel] ?? []) : []), [audiences, watchChannel]);
  const controlName = useMemo(() => `audiences.${watchChannelId}`, [watchChannelId]);
  const currentChannelAudiences = useMemo(
    () =>
      (watchChannelId != null ? watchAudiences?.[watchChannelId] : null) ??
      AudienceTargetingService.defaultAudienceTargeting,
    [watchAudiences, watchChannelId],
  );

  const [isRequestAudiencesModalOpen, setIsRequestAudiencesModalOpen] = useState<boolean>(false);
  const [templatesModal, setTemplatesModal] = useState<{ open: boolean; type: AudienceTemplateModalType }>({
    open: false,
    type: AudienceTemplateModalType.View,
  });

  const openTemplates = useCallback((type: AudienceTemplateModalType) => setTemplatesModal({ open: true, type }), []);

  const closeTemplates = useCallback(() => setTemplatesModal(state => ({ ...state, open: false })), []);

  const onAudienceChange = useCallback(
    (selectedAudiences: IBaseAudienceTargeting<IAudienceControlOption<number>>) => {
      setValue(controlName, selectedAudiences, { shouldDirty: true });
    },
    [controlName],
  );

  const onResetAudiences = useCallback(() => {
    onAudienceChange(AudienceTargetingService.defaultAudienceTargeting);
  }, [onAudienceChange]);

  const onRequestAudiences = useCallback(() => {
    setIsRequestAudiencesModalOpen(true);
  }, []);

  useEffect(() => {
    if (!audiences) {
      dispatch(getAudiences());
    }
  }, [audiences, dispatch]);

  useEffect(() => {
    dispatch(getBeeswaxFirstPartySegments());
  }, [dispatch]);

  return (
    <div className='flex flex-col w-full'>
      {!watchChannel ? (
        <p>Please select a channel on the &quot;{LineItemFormTab.Basic}&quot; tab first</p>
      ) : (
        <>
          <AudienceHeader
            onResetAudience={onResetAudiences}
            onRequestAudiences={onRequestAudiences}
            templatesEnabled
            onCreateTemplate={isAdmin ? () => openTemplates(AudienceTemplateModalType.Create) : undefined}
            onViewTemplates={() => openTemplates(AudienceTemplateModalType.View)}
          />
          <AudienceListContainer
            allowExtendedGrouping={extendedAudiences}
            onChange={onAudienceChange}
            options={options}
            value={currentChannelAudiences}
            loading={loadingAudiences}
            containerSx={{ paddingTop: '15px' }}
          />
        </>
      )}
      <FormLabel htmlFor='first-party-segments-select' sx={{ fontWeight: 600, marginTop: '18px' }}>
        First Party Segments
      </FormLabel>
      <Controller
        name='firstPartySegments'
        control={control}
        defaultValue={[]}
        render={({ field, fieldState }) => (
          <Autocomplete
            {...field}
            id='first-party-segments-select'
            multiple
            limitTags={3}
            loading={loadingFirstPartySegments}
            disableCloseOnSelect
            options={firstPartySegments}
            getOptionLabel={option => option?.label || ''}
            getOptionKey={option => option.value}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            renderOption={(props, option, { selected }) => {
              const { key, ...optionProps } = props;
              return (
                <li key={key} {...optionProps}>
                  <Checkbox style={{ marginRight: 8 }} checked={selected} />
                  <span>{option.label}</span>&nbsp;
                  <span className='text-gray-500'>(ID: {option.displayKey})</span>
                </li>
              );
            }}
            onChange={(_, value) => field.onChange(value)}
            renderInput={props => (
              <TextField {...props} placeholder='Select First Party Segments' {...getFieldErrorProps(fieldState)} />
            )}
          />
        )}
      />
      <SupportRequestModal
        title='Audiences'
        isOpen={isRequestAudiencesModalOpen}
        handleClose={() => setIsRequestAudiencesModalOpen(false)}
      />
      <AudienceTemplatesModal
        open={templatesModal.open}
        initialType={templatesModal.type}
        options={options}
        currentValue={currentChannelAudiences}
        allowExtendedGrouping={extendedAudiences}
        onClose={closeTemplates}
        onApply={onAudienceChange}
      />
    </div>
  );
};

export default LineItemFormAudienceSection;
